import React, { useState, useEffect, useContext } from "react";
import { TextField, IconButton, MenuItem, Button, Box, Typography } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import Commonmodel from "../../Commonmodel/Commonmodel";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { authHttp } from "../../../services/commonHttpService";
import { apisPath } from "../../../utils/path";
import { CommonContext } from "../../../context/CommonContext";
import { formatSlotTime } from "../../../helpers/commonHelper";
import SuccessImg from "../../Images/success_img.svg";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// Validation schema
const bookingSchema = Yup.object().shape({
  appointments: Yup.array()
    .of(
      Yup.object().shape({
        date: Yup.string(),
        slot_id: Yup.string(),
      })
    )
    .test(
      "at-least-one-complete",
      "Please select at least one date and slot",
      function (appointments) {
        return appointments.some(
          (apt) => apt?.date && apt?.slot_id
        );
      }
    ),
});

const BookAppointmentModal = ({
  open,
  onClose,
  patientId,
  patientName,
  onSuccess,
  planned_chemo_cycles,
  existingSessionsCount = 0
}) => {
  const { setIsLoading, setAlertState, setSnackState } = useContext(CommonContext);
  const [slots, setSlots] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch slots on mount
  useEffect(() => {
    if (open) {
      fetchSlots();
      setShowSuccess(false);
    }
  }, [open]);

  const fetchSlots = async () => {
    setIsLoading(true);
    try {
      const response = await authHttp.get(apisPath?.getAllSlots);
      if (response?.data?.status) {
        // Filter only slots with available beds > 0
        const availableSlots = response?.data?.data?.filter(
          (slot) => slot?.available_beds > 0
        );
        setSlots(availableSlots);
      }
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || "Failed to fetch slots",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get unique available dates
  const getAvailableDates = () => {
    const uniqueDates = [...new Set(slots.map((slot) => slot?.date))];
    return uniqueDates;
  };

  // Get min and max dates for calendar
  const getDateRange = () => {
    const availableDates = getAvailableDates();
    if (availableDates.length === 0) return { min: "", max: "" };

    const sortedDates = availableDates.sort();
    return {
      min: sortedDates[0],
      max: sortedDates[sortedDates.length - 1]
    };
  };

  // Check if a date is available
  const isDateAvailable = (dateString) => {
    return getAvailableDates().includes(dateString);
  };

  // Get slots for a specific date
  const getSlotsForDate = (date) => {
    return slots.filter((slot) => slot?.date === date);
  };

  // Check if slot is already selected in other rows
  const isSlotSelected = (slotId, currentIndex, values) => {
    return values?.appointments.some(
      (apt, index) => index !== currentIndex && apt?.slot_id === slotId
    );
  };

  // Handle Add More button click with validation
  const handleAddMore = (push, currentAppointmentsCount) => {
    // Calculate total sessions after adding new one
    const totalAfterAdding = existingSessionsCount + currentAppointmentsCount + 1;

    // Check if it would exceed planned cycles
    if (totalAfterAdding > planned_chemo_cycles) {
      setSnackState({
        open: true,
        message: "You have booked all the sessions suggested by TCCC.",
        severity: "info",
      });
      // return;
    }

    // Add new row
    push({ date: "", slot_id: "" });
  };

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);

      // Filter out incomplete appointments and prepare payload
      const payload = values?.appointments
        .filter((apt) => apt?.date && apt?.slot_id)
        .map((apt) => ({
          patient_id: patientId,
          slot_id: parseInt(apt?.slot_id),
        }));

      if (payload.length === 0) {
        setSnackState({
          open: true,
          message: "Please select at least one date and slot",
          severity: "error",
        });
        setIsLoading(false);
        return;
      }

      const response = await authHttp.post(apisPath?.bookPatientAppointment, payload);

      if (response?.data?.status) {
        // Show success screen
        setShowSuccess(true);

        // Navigate after 2 seconds
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (error) {
      setAlertState({
        open: true,
        title: error?.response?.data?.message || "Failed to book appointment",
        btnTxt: "CLOSE",
        status: "fail",
        onClose: () => {
          onSuccess();
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const dateRange = getDateRange();

  return (
    <Formik
      key={open ? 'open' : 'closed'} // Forces remount when modal opens/closes
      initialValues={{
        appointments: [{ date: "", slot_id: "" }],
      }}
      validationSchema={bookingSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, setFieldValue, submitForm }) => (
        <Commonmodel
          open={open}
          onClose={onClose}
          title={showSuccess ? "" : `Book Appointment For ${patientName}`}
          submitLabel="Book Appointment"
          showActions={!showSuccess}
          onSubmit={submitForm}
        >
          {showSuccess ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
              }}
            >
              {/* Success Image */}
              <Box sx={{ mb: 3 }}>
                <img
                  src={SuccessImg}
                  alt="Success"
                  style={{ width: "150px", height: "150px" }}
                />
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  textAlign: "center",
                }}
              >
                Appointment Booked Successfully
              </Typography>

              <Box
                sx={{
                  backgroundColor: "#e3f2fd",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#1976d2",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span style={{ fontSize: "18px" }}>💬</span>
                  SMS/WhatsApp notifications has been sent on registered Mobile Number
                </Typography>
              </Box>
            </Box>
          ) : (
            <Form>
              <p className="text-muted mb-3">
                Book an appointment with available date & slot.
              </p>

              <FieldArray name="appointments">
                {({ push, remove }) => (
                  <>
                    {values.appointments.map((appointment, index) => {
                      const slotsForDate = appointment?.date
                        ? getSlotsForDate(appointment?.date)
                        : [];

                      return (
                        <div className="row mb-3 align-items-end" key={index}>
                          {/* Appointment Date - Calendar */}
                          <div className="col-md-5 col-12 mb-2">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                views={["year", "month", "day"]}
                                label="Appointment Date"
                                format="DD-MM-YYYY"
                                value={appointment?.date ? dayjs(appointment?.date) : null}
                                minDate={dateRange?.min ? dayjs(dateRange?.min) : null}
                                maxDate={dateRange?.max ? dayjs(dateRange?.max) : null}
                                onChange={(newValue) => {
                                  if (!newValue) {
                                    setFieldValue(`appointments.${index}.date`, "");
                                    return;
                                  }

                                  const selectedDate = dayjs(newValue).format("YYYY-MM-DD");

                                  if (isDateAvailable(selectedDate)) {
                                    setFieldValue(`appointments.${index}.date`, selectedDate);
                                    setFieldValue(`appointments.${index}.slot_id`, "");
                                  } else {
                                    setSnackState({
                                      open: true,
                                      message: "No slots available for the selected date. Please choose another date.",
                                      severity: "error",
                                    });
                                    setFieldValue(`appointments.${index}.date`, "");
                                  }
                                }}
                                slotProps={{
                                  textField: {
                                    variant: "standard",    // <-- underline only
                                    fullWidth: true,
                                    autoComplete: "off",
                                    onDrop: (e) => {
                                      e.preventDefault();
                                      return false;
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </div>

                          {/* Slot Dropdown */}
                          <div className="col-md-6 col-12 mb-2">
                            <TextField
                              select
                              label="Available Slot"
                              fullWidth
                              value={appointment?.slot_id}
                              onChange={(e) => {
                                setFieldValue(`appointments.${index}.slot_id`, e.target.value);
                              }}
                              disabled={!appointment?.date}
                            >
                              <MenuItem value="">
                                <em>Select Slot</em>
                              </MenuItem>
                              {slotsForDate.map((slot) => {
                                const isDisabled = isSlotSelected(
                                  slot.slot_id.toString(),
                                  index,
                                  values
                                );
                                return (
                                  <MenuItem
                                    key={slot.slot_id}
                                    value={slot.slot_id.toString()}
                                    disabled={isDisabled}
                                  >
                                    {formatSlotTime(slot.start_time, slot.end_time)}
                                    {isDisabled && " (Already selected)"}
                                  </MenuItem>
                                );
                              })}
                            </TextField>
                          </div>

                          {/* Delete Button */}
                          <div className="col-md-1 col-2 mb-2">
                            {values.appointments.length > 1 && (
                              <IconButton
                                color="error"
                                onClick={() => remove(index)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Validation Error */}
                    {errors.appointments && touched.appointments && (
                      <Typography color="error" variant="caption" sx={{ mb: 2, display: "block" }}>
                        {errors.appointments}
                      </Typography>
                    )}

                    {/* Add Row Button - with validation */}
                    <Button
                      variant="outlined"
                      startIcon={<AddCircleIcon />}
                      onClick={() => handleAddMore(push, values.appointments.length)}
                      sx={{ borderRadius: "20px" }}
                    >
                      Add More
                    </Button>
                  </>
                )}
              </FieldArray>
            </Form>
          )}
        </Commonmodel>
      )}
    </Formik>
  );
};

export default BookAppointmentModal;